import { parse } from "yaml";

import { resolvedName, type Dependency } from "./manifest.ts";

/** The solution in a `dependencies.lock` the component manager wrote. */

export interface LockedComponent {
    name: string;
    version: string;
    source: "registry" | "git" | "path" | "idf";
    /** The repository of a Git component, or the directory of a local one. */
    location?: string;
    /** The names of the components it depends on, in the lock's order. */
    requires: string[];
}

export interface Lock {
    /** The chip the solution is for. */
    target: string | undefined;
    components: Map<string, LockedComponent>;
}

const LOCK_SOURCES: Record<string, LockedComponent["source"]> = {
    service: "registry",
    git: "git",
    local: "path",
    idf: "idf",
};

/** The solution a `dependencies.lock` holds, or undefined when it cannot be read. */
export function parseLock(source: string): Lock | undefined {
    let data: unknown;
    try {
        data = parse(source);
    } catch {
        return undefined;
    }
    if (typeof data !== "object" || data === null) return undefined;
    const lock = data as {
        target?: unknown;
        dependencies?: Record<
            string,
            { version?: unknown; source?: Record<string, unknown>; dependencies?: { name?: unknown }[] }
        >;
    };
    const components = new Map<string, LockedComponent>();
    for (const [name, entry] of Object.entries(lock.dependencies ?? {})) {
        const type = String(entry?.source?.type ?? "service");
        const location = entry?.source?.git ?? entry?.source?.path;
        components.set(name, {
            name,
            version: entry?.version === undefined ? "" : String(entry.version),
            source: LOCK_SOURCES[type] ?? "registry",
            location: location === undefined ? undefined : String(location),
            requires: (Array.isArray(entry?.dependencies) ? entry.dependencies : [])
                .map((d) => String(d?.name ?? ""))
                .filter((d) => d !== "" && d !== "idf"),
        });
    }
    return {
        target: typeof lock.target === "string" ? lock.target : undefined,
        components,
    };
}

/** The component `dep` resolved to, looked up by the name the component manager gives it. */
export function lockedComponent(
    lock: Lock | undefined,
    dep: Pick<Dependency, "name" | "source">,
): LockedComponent | undefined {
    if (lock === undefined) return undefined;
    return lock.components.get(dep.name) ?? lock.components.get(resolvedName(dep));
}
